<template>
  <div class="bots-page">
    <!-- Background Elements -->
    <div class="background-elements">
      <div class="floating-orb orb-1"></div>
      <div class="floating-orb orb-2"></div>
      <div class="floating-orb orb-3"></div>
    </div>



    <!-- Main Content -->
    <div class="main-content">
      <!-- Active Agents Section -->
      <div class="section">
        <div class="section-header">
          <h2 class="section-title">
            <i class="ri-robot-2-line"></i>
            {{ directT('bots.yourAiTeam') }}
          </h2>
          <div class="section-actions">
            <button class="filter-btn">
              <i class="ri-filter-3-line"></i>
              {{ directT('bots.filter') }}
            </button>
            <button class="view-toggle-btn" @click="toggleView">
              <i :class="isGridView ? 'ri-list-unordered' : 'ri-grid-line'"></i>
            </button>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="loading-container">
          <div class="loading-spinner"></div>
          <p class="loading-text">{{ directT('bots.loading') || 'Loading agents...' }}</p>
        </div>

        <!-- Empty State -->
        <div v-else-if="!loading && filteredAgents.length === 0" class="empty-state">
          <div class="empty-icon">
            <i class="ri-robot-line"></i>
          </div>
          <h3 class="empty-title">{{ directT('bots.noAgents') || 'No agents found' }}</h3>
          <p class="empty-description">{{ directT('bots.noAgentsDescription') || 'Create your first AI agent to get started.' }}</p>
        </div>

        <div class="agents-grid" v-else-if="isGridView">
          <div
            v-for="(agent, index) in filteredAgents"
            :key="agent.id"
            class="agent-card"
            :style="{ animationDelay: `${index * 0.1}s` }"
          >
            <!-- Agent Header -->
            <div class="agent-header" :class="agent.gradientClass">
              <div class="agent-status">
                <div class="status-indicator active"></div>
                <span class="status-text">{{ directT('bots.status.active') }}</span>
              </div>
              <div class="agent-avatar">
                <img :src="agent.image || '/images/bots/default.png'" :alt="agent.name" />
              </div>
            </div>

            <!-- Agent Content -->
            <div class="agent-content">
              <div class="agent-info">
                <div class="agent-department">{{ agent.department }}</div>
                <h3 class="agent-name">{{ agent.name }}</h3>
              </div>

              <!-- Agent Description -->
              <div class="agent-description">
                <div class="description-section">
                  <div class="description-header">
                    <i class="ri-information-line"></i>
                    <span class="description-title">{{ directT('bots.description') }}</span>
                  </div>
                  <p class="description-text">{{ agent.description }}</p>
                  </div>
                </div>

              <!-- Agent Footer -->
              <div class="agent-footer">
                <router-link :to="`/bots/settings/${agent.id}`" class="bot-settings-btn" :class="agent.gradientClass">
                  <i class="ri-settings-4-line"></i>
                  <span>{{ directT('bots.botSettings') }}</span>
                </router-link>
                  </div>
                  </div>
                </div>
                  </div>

        <!-- List View -->
        <div class="agents-list" v-else-if="!loading && filteredAgents.length > 0">
          <div
            v-for="(agent, index) in filteredAgents"
            :key="agent.id"
            class="agent-list-item"
            :style="{ animationDelay: `${index * 0.1}s` }"
          >
            <div class="agent-list-avatar">
              <img :src="agent.image || '/images/bots/default.png'" :alt="agent.name" />
              <div class="status-indicator active"></div>
                  </div>
            
            <div class="agent-list-content">
              <div class="agent-list-header">
                <h3 class="agent-list-name">{{ agent.name }}</h3>
                <span class="agent-list-department">{{ agent.department }}</span>
                </div>
              <p class="agent-list-description">{{ agent.description }}</p>
              </div>

            <div class="agent-list-actions">
              <router-link :to="`/bots/settings/${agent.id}`" class="bot-settings-btn-list" :class="agent.gradientClass">
                    <i class="ri-settings-4-line"></i>
                <span>{{ directT('bots.settings') }}</span>
                  </router-link>
            </div>
          </div>
        </div>
      </div>

      <!-- Marketplace Section -->
      <div class="section marketplace-section">
        <!-- Coming Soon Overlay -->
        <div class="coming-soon-overlay">
          <div class="coming-soon-content">
            <div class="coming-soon-icon">
              <i class="ri-time-line"></i>
            </div>
            <h3 class="coming-soon-title">{{ directT('bots.comingSoon.title') }}</h3>
            <p class="coming-soon-description">{{ directT('bots.comingSoon.description') }}</p>
          </div>
        </div>
        
        <div class="section-header">
          <h2 class="section-title">
            <i class="ri-store-2-line"></i>
            {{ directT('bots.agentStore') }}
          </h2>
          <button class="action-btn secondary-btn">
            <i class="ri-external-link-line"></i>
            {{ directT('bots.visitFullStore') }}
          </button>
        </div>

        <div class="marketplace-grid">
          <div
            v-for="(agent, index) in marketplaceAgents"
            :key="index"
            class="marketplace-card"
            :style="{ animationDelay: `${index * 0.1}s` }"
          >
            <!-- Marketplace Agent Header -->
            <div class="marketplace-header" :class="agent.gradientClass">
              <div class="marketplace-category">{{ agent.category }}</div>
              <div class="marketplace-avatar">
                <img :src="agent.image" :alt="agent.name" />
              </div>
            </div>

            <!-- Marketplace Agent Content -->
            <div class="marketplace-content">
              <h3 class="marketplace-name">{{ agent.name }}</h3>
              <p class="marketplace-description">{{ agent.description }}</p>

              <!-- Features -->
              <div class="marketplace-features">
                <div 
                  v-for="(feature, fIndex) in agent.features.slice(0, 2)" 
                  :key="fIndex"
                  class="feature-item"
                >
                  <i class="ri-checkbox-circle-line"></i>
                  <span>{{ feature }}</span>
                </div>
              </div>

              <!-- Marketplace Footer -->
              <div class="marketplace-footer">
                <div class="marketplace-price">{{ agent.price }}</div>
                <button class="add-agent-btn">
                  <i class="ri-add-line"></i>
                  {{ directT('bots.add') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useI18n } from "vue-i18n";
import { supabase } from '@/lib/supabaseClient';
import { getCurrentOrgId } from '@/utils/getCurrentOrgId';
import enTranslations from "@/i18n/locales/en.json";
import heTranslations from "@/i18n/locales/he.json";
import arTranslations from "@/i18n/locales/ar.json";

// Type definition for translation objects
type TranslationObject = {
  [key: string]: string | TranslationObject;
};

const searchQuery = ref('');
const isGridView = ref(true);
const loading = ref(false);

const toggleView = () => {
  isGridView.value = !isGridView.value;
};

// i18n
const { locale } = useI18n();

// Translation files
const translationFiles = {
  en: enTranslations,
  he: heTranslations,
  ar: arTranslations
};

// Direct translation function that bypasses vue-i18n
const directT = (key: string): string => {
  const parts = key.split('.');
  let result: TranslationObject = translationFiles[locale.value as keyof typeof translationFiles];
  
  for (const part of parts) {
    if (!result || !(part in result)) {
      console.warn(`Missing translation key: ${key} in ${locale.value} locale`);
      // Fallback to English if translation not found
      let enResult: TranslationObject = translationFiles.en;
      for (const fallbackPart of parts) {
        if (!enResult || !(fallbackPart in enResult)) return key;
        enResult = enResult[fallbackPart as keyof typeof enResult] as TranslationObject;
      }
      return typeof enResult === 'string' ? enResult : key;
    }
    result = result[part as keyof typeof result] as TranslationObject;
  }
  return typeof result === 'string' ? result : key;
};



interface AgentStat {
  label: string;
  value: string;
  icon?: string;
}

interface Agent {
  id: string;
  name: string;
  icon?: string;
  image?: string;
  iconClasses?: string;
  gradientClass?: string;
  statusClass?: string;
  stat1: AgentStat;
  stat2: AgentStat;
  stat3: AgentStat;
  linkedTo: string;
  department: string;
  description: string;
  roles: string[];
}

interface MarketplaceAgent {
  name: string;
  icon?: string;
  image?: string;
  iconClasses?: string;
  gradientClass?: string;
  category: string;
  description: string;
  features: string[];
  price: string;
}

// Reactive agents data from Supabase
const agentsData = ref<Agent[]>([]);

// Function to get random gradient class
const getRandomGradientClass = () => {
  const gradients = ['gradient-primary', 'gradient-purple', 'gradient-info', 'gradient-success', 'gradient-warning', 'gradient-teal'];
  return gradients[Math.floor(Math.random() * gradients.length)];
};

// Function to get random department
const getRandomDepartment = () => {
  const departments = ['Personal', 'Sales', 'Customer Service', 'Sales and Marketing', 'Finance and Accounts'];
  return departments[Math.floor(Math.random() * departments.length)];
};

// Function to get random image
const getRandomImage = () => {
  const images = [
    '/images/bots/personal-assistant.png',
    '/images/bots/followup.png',
    '/images/bots/secretary.png',
    '/images/bots/customer-service.png',
    '/images/bots/sales.png',
    '/images/bots/finance.png'
  ];
  return images[Math.floor(Math.random() * images.length)];
};

// Fetch agents from Supabase
const fetchAgents = async () => {
  try {
    loading.value = true;
    
    // Get current organization ID
    const orgId = await getCurrentOrgId();
    if (!orgId) {
      console.error('No organization ID found');
      return;
    }

    const { data, error } = await supabase
      .from('agents')
      .select('*')
      .eq('organization_id', orgId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching agents:', error);
      return;
    }

    // Map Supabase data to Agent interface
    agentsData.value = data?.map((agent: any) => ({
      id: agent.id,
      name: agent.bot_name_value || 'Unnamed Agent',
      image: getRandomImage(), // Keep random images as requested
      gradientClass: getRandomGradientClass(),
    statusClass: "bg-success-100 text-success-700",
    stat1: { 
      label: "Active Tasks",
        value: Math.floor(Math.random() * 50).toString(),
      icon: "ri-checkbox-circle-line"
    },
    stat2: { 
      label: "Response Time",
        value: `${(Math.random() * 3 + 1).toFixed(1)}s`,
      icon: "ri-flashlight-line"
    },
    stat3: { 
      label: "Accuracy",
        value: `${Math.floor(Math.random() * 10 + 90)}%`,
      icon: "ri-award-line"
    },
      linkedTo: agent.type || "General",
      department: getRandomDepartment(),
      description: agent.bot_description_value || 'AI Agent for various tasks and automation.',
      roles: [agent.type || "AI Assistant", "Task Manager", "Virtual Assistant"]
    })) || [];

  } catch (error) {
    console.error('Error fetching agents:', error);
  } finally {
    loading.value = false;
  }
};

// Fetch agents on component mount
onMounted(() => {
  fetchAgents();
});

const marketplaceAgents: MarketplaceAgent[] = [
  {
    name: "Data Analyst",
    image: "/images/bots/data-analyst.png",
    gradientClass: "gradient-purple",
    category: "Data Analysis",
    description: "Smart agent that analyzes business data and provides automated insights",
    features: [
      "Trend and Pattern Analysis",
      "Automated Reports",
      "Alerts for Outliers"
    ],
    price: "199₪ / Month"
  },
  {
    name: "Content Assistant",
    image: "/images/bots/content-assistant.png",
    gradientClass: "gradient-success",
    category: "Content",
    description: "Smart agent that creates and edits content marketing automatically",
    features: [
      "Blog Post Writing",
      "Editing Existing Content",
      "Target Audience Adaptation"
    ],
    price: "179₪ / Month"
  }
];

const filteredAgents = computed(() => {
  if (!searchQuery.value) return agentsData.value;
  return agentsData.value.filter(agent => 
    agent.name.includes(searchQuery.value) || 
    agent.department.includes(searchQuery.value)
  );
});
</script>

<style scoped>
/* Main Container */
.bots-page {
  min-height: 100vh;
  background: white;
  padding: 40px;
  position: relative;
  overflow: hidden;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* Dark mode background */
.dark .bots-page {
  background: linear-gradient(to bottom right, #0c1427, #0c1427);
}

/* Background Elements */
.background-elements {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 0;
}

.floating-orb {
  position: absolute;
  border-radius: 50%;
  background: linear-gradient(45deg, rgba(139, 92, 246, 0.05), rgba(59, 130, 246, 0.05));
  backdrop-filter: blur(20px);
  animation: float 6s ease-in-out infinite;
}

.orb-1 {
  width: 300px;
  height: 300px;
  top: -150px;
  right: -150px;
  animation-delay: 0s;
}

.orb-2 {
  width: 200px;
  height: 200px;
  bottom: -100px;
  left: -100px;
  animation-delay: 2s;
}

.orb-3 {
  width: 150px;
  height: 150px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  animation-delay: 4s;
}

@keyframes float {
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(180deg); }
}



/* Main Content */
.main-content {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 48px;
  max-width: 1600px;
  margin: 0 auto;
  padding: 0 20px;
}

/* Section */
.section {
  background: transparent;
  border: none;
  border-radius: 0;
  padding: 0;
  box-shadow: none;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 40px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 24px;
  padding: 32px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  max-width: 1600px;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 40px;
}

/* Dark mode section header */
.dark .section-header {
  background: linear-gradient(135deg, 
    rgba(15, 23, 42, 0.9) 0%, 
    rgba(30, 58, 138, 0.3) 50%, 
    rgba(37, 99, 235, 0.2) 100%);
  border-color: rgba(59, 130, 246, 0.2);
}

.section-title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

/* Dark mode section title */
.dark .section-title {
  color: #f9fafb;
}

.section-title i {
  font-size: 1.5rem;
  color: #8b5cf6;
}

.section-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.filter-btn,
.view-toggle-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  color: #1f2937;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

/* Dark mode filter and view toggle buttons */
.dark .filter-btn,
.dark .view-toggle-btn {
  background: rgba(15, 23, 42, 0.6);
  border-color: rgba(59, 130, 246, 0.2);
  color: #f9fafb;
}

.filter-btn:hover,
.view-toggle-btn:hover {
  background: rgba(255, 255, 255, 1);
  transform: translateY(-1px);
}

/* Dark mode filter and view toggle buttons hover */
.dark .filter-btn:hover,
.dark .view-toggle-btn:hover {
  background: rgba(15, 23, 42, 0.8);
  border-color: rgba(59, 130, 246, 0.3);
}

/* Loading State */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
}

.loading-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid rgba(139, 92, 246, 0.2);
  border-top: 4px solid #8b5cf6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-text {
  color: #6b7280;
  font-size: 1rem;
  margin: 0;
}

/* Dark mode loading text */
.dark .loading-text {
  color: #9ca3af;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
}

.empty-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #8b5cf6, #3b82f6);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  font-size: 2rem;
  color: white;
}

.empty-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 8px 0;
}

/* Dark mode empty title */
.dark .empty-title {
  color: #f9fafb;
}

.empty-description {
  color: #6b7280;
  font-size: 1rem;
  margin: 0;
  max-width: 400px;
}

/* Dark mode empty description */
.dark .empty-description {
  color: #9ca3af;
}

/* Agents Grid */
.agents-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;
  max-width: 1600px;
  margin: 0 auto;
  padding: 0 20px;
  justify-items: center;
}

.agent-card {
  background: white;
  border: none;
  border-radius: 24px;
  overflow: hidden;
  transition: all 0.3s ease;
  animation: slideInUp 0.6s ease-out forwards;
  opacity: 0;
  transform: translateY(30px);
  box-shadow: 
    0 12px 40px rgba(0, 0, 0, 0.15),
    0 4px 12px rgba(0, 0, 0, 0.1),
    0 0 0 1px rgba(255, 255, 255, 0.9);
  position: relative;
  margin-bottom: 8px;
  width: 100%;
  max-width: 420px;
  min-height: 480px;
}

/* Dark mode agent card */
.dark .agent-card {
  background: linear-gradient(135deg, 
    rgba(15, 23, 42, 0.9) 0%, 
    rgba(30, 58, 138, 0.3) 50%, 
    rgba(37, 99, 235, 0.2) 100%);
  border: 1px solid rgba(59, 130, 246, 0.2);
  box-shadow: 
    0 12px 40px rgba(0, 0, 0, 0.3),
    0 4px 12px rgba(0, 0, 0, 0.2),
    0 0 0 1px rgba(59, 130, 246, 0.1);
}

.agent-card:hover {
  transform: translateY(-16px);
  box-shadow: 
    0 32px 80px rgba(0, 0, 0, 0.2),
    0 12px 32px rgba(0, 0, 0, 0.15),
    0 0 0 1px rgba(255, 255, 255, 1);
}

/* Agent Header */
.agent-header {
  height: 140px;
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 20px;
}

.gradient-primary {
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.3), rgba(59, 130, 246, 0.3));
}

.gradient-purple {
  background: linear-gradient(135deg, rgba(168, 85, 247, 0.3), rgba(139, 92, 246, 0.3));
}

.gradient-info {
  background: linear-gradient(135deg, rgba(6, 182, 212, 0.3), rgba(8, 145, 178, 0.3));
}

.gradient-success {
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.3), rgba(5, 150, 105, 0.3));
}

.gradient-warning {
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.3), rgba(217, 119, 6, 0.3));
}

.gradient-teal {
  background: linear-gradient(135deg, rgba(20, 184, 166, 0.3), rgba(13, 148, 136, 0.3));
}

.agent-status {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  padding: 6px 12px;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.status-indicator {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.status-indicator.active {
  background: #10b981;
  box-shadow: 0 0 8px rgba(16, 185, 129, 0.5);
}

.status-text {
  font-size: 0.8rem;
  color: #1f2937;
  font-weight: 600;
}

.agent-avatar {
  width: 90px;
  height: 90px;
  border-radius: 18px;
  overflow: hidden;
  border: 2px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
}

.agent-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Agent Content */
.agent-content {
  padding: 28px;
  display: flex;
  flex-direction: column;
  height: calc(100% - 140px); /* Subtract header height */
}

.agent-info {
  margin-bottom: 24px;
}

.agent-department {
  font-size: 0.8rem;
  color: #6b7280;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Dark mode agent department */
.dark .agent-department {
  color: #9ca3af;
}

.agent-name {
  font-size: 1.2rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

/* Dark mode agent name */
.dark .agent-name {
  color: #f9fafb;
}

/* Agent Description */
.agent-description {
  margin-bottom: 24px;
  padding: 16px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(248, 250, 252, 0.9));
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  flex-grow: 1; /* Take up remaining space */
}

/* Dark mode agent description */
.dark .agent-description {
  background: linear-gradient(135deg, rgba(15, 23, 42, 0.6), rgba(30, 41, 59, 0.7));
  border-color: rgba(59, 130, 246, 0.1);
}

.description-section {
  margin-bottom: 16px;
}

.description-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 8px;
}

/* Dark mode description header */
.dark .description-header {
  color: #f9fafb;
}

.description-header i {
  color: #3b82f6;
  font-size: 1rem;
}

.description-title {
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.description-text {
  font-size: 0.9rem;
  color: #4b5563;
  line-height: 1.6;
  margin: 0;
  text-align: left;
  direction: ltr;
}

/* Dark mode description text */
.dark .description-text {
  color: #cbd5e1;
}

/* Agent Footer */
.agent-footer {
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 20px;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  margin-top: auto; /* Push to bottom */
}

.bot-settings-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 12px;
  color: white;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  position: relative;
  overflow: hidden;
  width: 100%;
  justify-content: center;
}

.bot-settings-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05));
  border-radius: 12px;
  transition: all 0.3s ease;
}

.bot-settings-btn i,
.bot-settings-btn span {
  position: relative;
  z-index: 2;
}

.bot-settings-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.25);
}

.bot-settings-btn:hover::before {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.1));
}

/* Gradient backgrounds for bot settings button */
.bot-settings-btn.gradient-primary {
  background: linear-gradient(135deg, #8b5cf6, #3b82f6);
}

.bot-settings-btn.gradient-purple {
  background: linear-gradient(135deg, #a855f7, #8b5cf6);
}

.bot-settings-btn.gradient-info {
  background: linear-gradient(135deg, #06b6d4, #0891b2);
}

.bot-settings-btn.gradient-success {
  background: linear-gradient(135deg, #10b981, #059669);
}

.bot-settings-btn.gradient-warning {
  background: linear-gradient(135deg, #f59e0b, #d97706);
}

.bot-settings-btn.gradient-teal {
  background: linear-gradient(135deg, #14b8a6, #0d9488);
}

/* Marketplace Section */
.marketplace-section {
  background: transparent;
  border: none;
  position: relative;
}

/* Coming Soon Overlay */
.coming-soon-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.75);
  backdrop-filter: blur(6px);
  border-radius: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  animation: fadeIn 0.5s ease-out;
}

/* Dark mode coming soon overlay */
.dark .coming-soon-overlay {
  background: rgba(15, 23, 42, 0.75);
}

.coming-soon-content {
  text-align: center;
  padding: 40px;
  max-width: 400px;
}

.coming-soon-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #8b5cf6, #3b82f6);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  font-size: 2rem;
  color: white;
  box-shadow: 
    0 8px 24px rgba(139, 92, 246, 0.3),
    0 4px 12px rgba(139, 92, 246, 0.2);
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
}

.coming-soon-title {
  font-size: 2rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 16px 0;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  letter-spacing: -0.02em;
}

/* Dark mode coming soon title */
.dark .coming-soon-title {
  color: #f9fafb;
}

.coming-soon-description {
  font-size: 1.1rem;
  color: #6b7280;
  line-height: 1.6;
  margin: 0;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* Dark mode coming soon description */
.dark .coming-soon-description {
  color: #9ca3af;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

.marketplace-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 32px;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  justify-items: center;
}

.marketplace-card {
  background: white;
  border: none;
  border-radius: 20px;
  overflow: hidden;
  transition: all 0.3s ease;
  animation: slideInUp 0.6s ease-out forwards;
  opacity: 0;
  transform: translateY(30px);
  box-shadow: 
    0 10px 35px rgba(0, 0, 0, 0.12),
    0 3px 10px rgba(0, 0, 0, 0.08),
    0 0 0 1px rgba(255, 255, 255, 0.9);
  margin-bottom: 8px;
  width: 100%;
  max-width: 380px;
  min-height: 320px;
}

/* Dark mode marketplace card */
.dark .marketplace-card {
  background: linear-gradient(135deg, 
    rgba(15, 23, 42, 0.9) 0%, 
    rgba(30, 58, 138, 0.3) 50%, 
    rgba(37, 99, 235, 0.2) 100%);
  border: 1px solid rgba(59, 130, 246, 0.2);
  box-shadow: 
    0 10px 35px rgba(0, 0, 0, 0.3),
    0 3px 10px rgba(0, 0, 0, 0.2),
    0 0 0 1px rgba(59, 130, 246, 0.1);
}

.marketplace-card:hover {
  transform: translateY(-8px);
  box-shadow: 
    0 20px 50px rgba(0, 0, 0, 0.18),
    0 8px 20px rgba(0, 0, 0, 0.12),
    0 0 0 1px rgba(255, 255, 255, 1);
}

.marketplace-header {
  height: 100px;
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 16px;
}

.marketplace-category {
  background: rgba(139, 92, 246, 0.1);
  backdrop-filter: blur(10px);
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 0.75rem;
  color: #8b5cf6;
  font-weight: 600;
  border: 1px solid rgba(139, 92, 246, 0.2);
}

.marketplace-avatar {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  overflow: hidden;
  border: 2px solid rgba(255, 255, 255, 0.4);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
  position: relative;
}

.marketplace-avatar::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05));
  border-radius: 14px;
  z-index: 1;
  transition: all 0.3s ease;
}

.marketplace-card:hover .marketplace-avatar {
  transform: scale(1.1) rotate(-3deg);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
}

.marketplace-card:hover .marketplace-avatar::before {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.1));
}

.marketplace-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: relative;
  z-index: 2;
}

.marketplace-content {
  padding: 20px;
}

.marketplace-name {
  font-size: 1.1rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 8px 0;
}

/* Dark mode marketplace name */
.dark .marketplace-name {
  color: #f9fafb;
}

.marketplace-description {
  font-size: 0.85rem;
  color: #6b7280;
  line-height: 1.5;
  margin: 0 0 16px 0;
}

/* Dark mode marketplace description */
.dark .marketplace-description {
  color: #9ca3af;
}

.marketplace-features {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8rem;
  color: #4b5563;
}

/* Dark mode feature item */
.dark .feature-item {
  color: #cbd5e1;
}

.feature-item i {
  color: #10b981;
  font-size: 0.9rem;
}

.marketplace-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
}

/* Dark mode marketplace footer */
.dark .marketplace-footer {
  border-color: rgba(59, 130, 246, 0.2);
}

.marketplace-price {
  font-size: 0.9rem;
  font-weight: 600;
  color: #1f2937;
}

/* Dark mode marketplace price */
.dark .marketplace-price {
  color: #f9fafb;
}

.add-agent-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: linear-gradient(135deg, #8b5cf6, #3b82f6);
  border: none;
  border-radius: 12px;
  color: white;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.add-agent-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 16px rgba(139, 92, 246, 0.3);
}

/* Animations */
@keyframes slideInUp {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive Design */
@media (max-width: 1024px) {
  .bots-page {
    padding: 24px;
  }
  
  .header-content {
    flex-direction: column;
    gap: 24px;
  }
  
  .header-actions {
    width: 100%;
    justify-content: space-between;
  }
  
  .search-input {
    width: 250px;
  }
  

  
  .agents-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 32px;
    padding: 0 16px;
  }
  
  .agents-list {
    padding: 0 16px;
  }
  
  .agent-list-item {
    padding: 16px;
    gap: 16px;
  }
  
  .agent-list-avatar {
    width: 50px;
    height: 50px;
  }
  
  .marketplace-grid {
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
    gap: 24px;
    padding: 0 16px;
  }
}

@media (max-width: 768px) {
  .bots-page {
    padding: 16px;
  }
  
  .main-content {
    padding: 0 4px;
    max-width: 100%;
  }
  
  .section-header {
    padding: 20px 12px;
    margin-bottom: 24px;
    max-width: 100%;
    margin-left: 0;
    margin-right: 0;
  }
  
  .agents-grid {
    grid-template-columns: 1fr;
    gap: 24px;
    padding: 0 4px;
    max-width: 100%;
    margin: 0;
  }
  
  .agent-card {
    max-width: 100%;
    min-height: 420px;
    margin: 0;
  }
  
  .page-title {
    font-size: 2rem;
  }
  
  .title-icon {
    font-size: 2rem;
  }
  
  .header-content {
    padding: 24px;
  }
  
  .header-actions {
    flex-direction: column;
    gap: 16px;
  }
  
  .search-input {
    width: 100%;
  }
  

  
  .section {
    padding: 24px;
  }
  
  .section-header {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
  }
  
  .agents-grid,
  .marketplace-grid {
    grid-template-columns: 1fr;
  }
  
  .agent-card,
  .marketplace-card {
    margin: 0 auto;
    max-width: 400px;
  }
  
  .agents-list {
    padding: 0 8px;
    max-width: 100%;
    margin: 0;
  }
  
  .agent-list-item {
    padding: 16px;
    gap: 16px;
    flex-direction: column;
    text-align: center;
    margin: 0;
  }
  
  .agent-list-header {
    flex-direction: column;
    gap: 8px;
    margin-bottom: 12px;
  }
  
  .agent-list-description {
    -webkit-line-clamp: 3;
  }
}

@media (max-width: 480px) {
  .bots-page {
    padding: 12px;
  }
  
  .header-content {
    padding: 20px;
  }
  
  .page-title {
    font-size: 1.8rem;
    flex-direction: column;
    gap: 8px;
    text-align: center;
  }
  

  
  .section {
    padding: 16px;
  }
  
  .section-header {
    padding: 16px;
  }
  
  .agent-content,
  .marketplace-content {
    padding: 16px;
  }
  
  .agents-list {
    padding: 0 8px;
  }
  
  .agent-list-item {
    padding: 12px;
    gap: 12px;
  }
  
  .agent-list-avatar {
    width: 40px;
    height: 40px;
  }
  
  .agent-list-name {
    font-size: 1rem;
  }
  
  .agent-list-description {
    font-size: 0.8rem;
  }
  
  .bot-settings-btn-list {
    padding: 8px 16px;
    font-size: 0.8rem;
  }
}

/* Agents List */
.agents-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.agent-list-item {
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 16px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 20px;
  transition: all 0.3s ease;
  animation: slideInUp 0.6s ease-out forwards;
  opacity: 0;
  transform: translateY(30px);
  box-shadow: 
    0 4px 16px rgba(0, 0, 0, 0.08),
    0 2px 8px rgba(0, 0, 0, 0.04);
}

/* Dark mode agent list item */
.dark .agent-list-item {
  background: linear-gradient(135deg, 
    rgba(15, 23, 42, 0.9) 0%, 
    rgba(30, 58, 138, 0.3) 50%, 
    rgba(37, 99, 235, 0.2) 100%);
  border-color: rgba(59, 130, 246, 0.2);
  box-shadow: 
    0 4px 16px rgba(0, 0, 0, 0.2),
    0 2px 8px rgba(0, 0, 0, 0.1);
}

.agent-list-item:hover {
  transform: translateY(-2px);
  box-shadow: 
    0 8px 24px rgba(0, 0, 0, 0.12),
    0 4px 12px rgba(0, 0, 0, 0.08);
  border-color: rgba(0, 0, 0, 0.15);
}

/* Dark mode agent list item hover */
.dark .agent-list-item:hover {
  box-shadow: 
    0 8px 24px rgba(0, 0, 0, 0.3),
    0 4px 12px rgba(0, 0, 0, 0.2);
  border-color: rgba(59, 130, 246, 0.3);
}

.agent-list-avatar {
  position: relative;
  width: 60px;
  height: 60px;
  border-radius: 12px;
  overflow: hidden;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.agent-list-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.agent-list-avatar .status-indicator {
  position: absolute;
  top: -2px;
  right: -2px;
  width: 16px;
  height: 16px;
  border: 2px solid white;
  border-radius: 50%;
}

.agent-list-content {
  flex: 1;
  min-width: 0;
}

.agent-list-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}

.agent-list-name {
  font-size: 1.1rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

/* Dark mode agent list name */
.dark .agent-list-name {
  color: #f9fafb;
}

.agent-list-department {
  font-size: 0.8rem;
  color: #6b7280;
  background: rgba(107, 114, 128, 0.1);
  padding: 4px 8px;
  border-radius: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Dark mode agent list department */
.dark .agent-list-department {
  color: #9ca3af;
  background: rgba(156, 163, 175, 0.2);
}

.agent-list-description {
  font-size: 0.9rem;
  color: #4b5563;
  line-height: 1.5;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Dark mode agent list description */
.dark .agent-list-description {
  color: #cbd5e1;
}

.agent-list-actions {
  flex-shrink: 0;
}

.bot-settings-btn-list {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: 10px;
  color: white;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.85rem;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  position: relative;
  overflow: hidden;
}

.bot-settings-btn-list::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05));
  border-radius: 10px;
  transition: all 0.3s ease;
}

.bot-settings-btn-list i,
.bot-settings-btn-list span {
  position: relative;
  z-index: 2;
}

.bot-settings-btn-list:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.bot-settings-btn-list:hover::before {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.1));
}

/* Gradient backgrounds for list button */
.bot-settings-btn-list.gradient-primary {
  background: linear-gradient(135deg, #8b5cf6, #3b82f6);
}

.bot-settings-btn-list.gradient-purple {
  background: linear-gradient(135deg, #a855f7, #8b5cf6);
}

.bot-settings-btn-list.gradient-info {
  background: linear-gradient(135deg, #06b6d4, #0891b2);
}

.bot-settings-btn-list.gradient-success {
  background: linear-gradient(135deg, #10b981, #059669);
}

.bot-settings-btn-list.gradient-warning {
  background: linear-gradient(135deg, #f59e0b, #d97706);
}

.bot-settings-btn-list.gradient-teal {
  background: linear-gradient(135deg, #14b8a6, #0d9488);
}
</style> 
